import React, { useMemo } from 'react';

import { message } from '../../../types';
import {
  isRTLLanguageByCode,
  useComposer,
  useLanguage
} from '../../../hooks/index';
import {
  Payment,
  ViewMessage,
  QuickReply,
  Standard,
  Action,
  General,
  Carousel,
  Progress,
  Image,
  Video,
  Event,
  Dynamic,
  File,
  Location,
  Markdown
} from './ResponseTypes';
import TypingIndicator from './TypingIndicator';

/**
 * The component to render for each type of Message.
 * The default is Standard.
 */
const types = {
  standard: Standard,
  action: Action,
  'quick-reply': QuickReply,
  general: General,
  carousel: Carousel,
  image: Image,
  video: Video,
  content: ViewMessage,
  embed: ViewMessage,
  payment: Payment,
  loading: TypingIndicator,
  progress: Progress,
  event: Event,
  dynamic: Dynamic,
  file: File,
  location: Location,
  markdown: Markdown
};

const defaultFilter = ({
  message,
  handleButtonClick,
  handleButtonLocationClick,
  label,
  hasHadReply
}) => {
  return {
    'aria-label': label,
    role: 'section',
    type: message.type,
    composer: message.composer,
    content: message.content,
    handleButtonClick,
    handleButtonLocationClick,
    direction:
      message.direction === 'toClient' ? 'sentMessage' : 'recievedMessage',
    hasHadReply: hasHadReply
  };
};

const filterQuickReply = props => {
  return {
    ...defaultFilter(props),
    // Only show quick replies if it's part of the last message group.
    showResponses: props.groupIndex === props.groups.length - 1
  };
};

const filterProgress = props => {
  return {
    ...defaultFilter(props),
    // Only show loading if it's part of the last message group.
    isLoading: props.groupIndex === props.groups.length - 1
  };
};

const filterEmbed = props => {
  return {
    ...defaultFilter(props),
    id: props.message.id
  };
};

const filterContent = props => {
  return {
    ...defaultFilter(props),
    id: props.message.id
  };
};

/**
 * Prop filters to handle passing unique props to specific
 * messages types.
 */
const filters = {
  'quick-reply': filterQuickReply,
  progress: filterProgress,
  embed: filterEmbed,
  content: filterContent
};

const filterProps = props => {
  const filter = filters[props.message.type] || defaultFilter;

  return filter(props);
};

const Message = props => {
  const { language } = useComposer();
  const { language: language2 } = useLanguage();

  let languageCode = false;
  if (language) {
    // broadcast, edit broadcast, answer in faq, conversation preview
    languageCode = language.country_code;
  } else if (language2) {
    // builder - demo
    languageCode = language2.country_code;
  } else if (props.message?.language?.country_code) {
    // transition
    languageCode = props.message.language.country_code;
  }

  const rtl = !languageCode ? 'ltr' : isRTLLanguageByCode(languageCode);
  const Component = useMemo(() => types[props.message.type] || Standard, [
    props.message.type
  ]);

  return <Component {...filterProps(props)} rtl={rtl} />;
};

Message.propTypes = message;

export default Message;
